/**
 * EventCard — modern list card. Used on Topic, Search, and any secondary
 * feed page. Shows thumbnail (or category-tinted gradient fallback),
 * category pill, headline, summary, meta strip.
 *
 * Health-vertical events also get evidence-level, availability status
 * and preprint pills per Health News Agent §25-26.
 */
import { Link } from "react-router-dom";
import {
  Globe2, Plane, Sprout, Luggage, HeartPulse, Leaf, BarChart3, Landmark,
  FlaskConical, ShieldAlert, Beaker,
  Radio, Video, Mic, Camera, BookOpen, MapPin, AlertOctagon, MessageSquare,
} from "lucide-react";
import { timeAgo, primaryCategoryPath, categoryLabel } from "./theme";
import RegionChip from "./RegionChip";

const CAT_GRAD = {
  aviation:    "from-sky-400 to-blue-600",
  agriculture: "from-emerald-400 to-green-600",
  travel:      "from-violet-400 to-purple-600",
  food:        "from-amber-400 to-orange-600",
  nature:      "from-green-500 to-emerald-700",
  health:      "from-rose-400 to-red-600",
  medicine:    "from-rose-500 to-pink-600",
  diseases:    "from-red-500 to-rose-600",
  drugs:       "from-fuchsia-500 to-purple-600",
  treatments:  "from-purple-500 to-indigo-600",
  clinical_trials: "from-indigo-500 to-blue-600",
  business:    "from-yellow-400 to-amber-600",
  world:       "from-blue-400 to-indigo-600",
  india:       "from-orange-400 to-red-500",
};
const CAT_ICON = {
  aviation: Plane, agriculture: Sprout, travel: Luggage, health: HeartPulse,
  medicine: HeartPulse, diseases: ShieldAlert, drugs: FlaskConical,
  treatments: Beaker, clinical_trials: FlaskConical,
  nature: Leaf, business: BarChart3, india: Landmark,
};

const EVIDENCE_LABEL = {
  1: "Lab study", 2: "Animal study", 3: "Pilot study",
  4: "Observational", 5: "RCT / Phase III",
  6: "Meta-analysis", 7: "Regulatory approval", 8: "Standard of care",
};
const AVAILABILITY_LABEL = {
  standard_treatment:      "Standard treatment",
  approved_limited_use:    "Approved (limited)",
  approved_some_countries: "Approved (some countries)",
  clinical_trial_only:     "Clinical trial only",
  experimental:            "Experimental",
  preclinical:             "Preclinical",
  not_yet_available:       "Not yet available",
};

function Thumb({ src, cats, className = "" }) {
  const cat = primaryCategoryPath(cats);
  const Icon = CAT_ICON[cat] || Globe2;
  const grad = CAT_GRAD[cat] || "from-slate-300 to-slate-400";
  return (
    <div className={`w-full h-full ${className}`}>
      {src ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className={`w-full h-full bg-gradient-to-br ${grad} flex items-center justify-center`}>
          <Icon className="w-8 h-8 text-white/70" strokeWidth={1.4} />
        </div>
      )}
    </div>
  );
}

function ImportanceDot({ score }) {
  const s = score || 0;
  const cls =
    s >= 80 ? "bg-red-600"
    : s >= 60 ? "bg-amber-500"
    : s >= 40 ? "bg-slate-500"
    : "bg-slate-300";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} title={`importance ${s}`} />;
}

function HealthPills({ event }) {
  const pills = [];
  if (event.evidence_level && EVIDENCE_LABEL[event.evidence_level]) {
    pills.push(
      <span key="ev" className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
        <FlaskConical className="w-3 h-3" /> Evidence L{event.evidence_level} · {EVIDENCE_LABEL[event.evidence_level]}
      </span>
    );
  }
  if (event.availability_status && AVAILABILITY_LABEL[event.availability_status]) {
    pills.push(
      <span key="av" className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
        {AVAILABILITY_LABEL[event.availability_status]}
      </span>
    );
  }
  if (event.is_preprint) {
    pills.push(
      <span key="pre" className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-100">
        Preprint · not peer-reviewed
      </span>
    );
  }
  if (!pills.length) return null;
  return <div className="mt-1.5 flex flex-wrap gap-1.5">{pills}</div>;
}

// ── Sprint-4: content format + editorial + urgency + neighborhood ──

const FORMAT_STYLES = {
  live_blog:   { Icon: Radio,   label: "LIVE",       cls: "bg-red-600 text-white",             pulse: true },
  video:       { Icon: Video,   label: "Video",      cls: "bg-slate-800 text-white" },
  podcast:     { Icon: Mic,     label: "Podcast",    cls: "bg-purple-700 text-white" },
  photo_story: { Icon: Camera,  label: "Photos",     cls: "bg-slate-700 text-white" },
  explainer:   { Icon: BookOpen,label: "Explainer",  cls: "bg-indigo-600 text-white" },
};

const EDITORIAL_STYLES = {
  opinion:      { label: "OPINION",   cls: "bg-amber-800 text-white" },
  editorial:    { label: "EDITORIAL", cls: "bg-amber-800 text-white" },
  analysis:     { label: "ANALYSIS",  cls: "bg-amber-700 text-white" },
  column:       { label: "COLUMN",    cls: "bg-amber-700 text-white" },
  guest_essay:  { label: "GUEST ESSAY", cls: "bg-amber-700 text-white" },
  sponsored:    { label: "SPONSORED", cls: "bg-slate-500 text-white" },
  press_release:{ label: "PRESS RELEASE", cls: "bg-slate-500 text-white" },
};

function MetaBadges({ event }) {
  const pills = [];
  // 1) Format (highest visibility — LIVE ribbon, video, etc.)
  const fmt = FORMAT_STYLES[event.content_format];
  if (fmt) {
    pills.push(
      <span key="fmt" className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${fmt.cls}`}
            data-testid={`event-format-${event.event_id}-${event.content_format}`}>
        {fmt.pulse && <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
        </span>}
        <fmt.Icon className="w-2.5 h-2.5" /> {fmt.label}
      </span>
    );
  }
  // 2) Editorial label (opinion/editorial/analysis/etc.)
  const ed = EDITORIAL_STYLES[event.editorial_label];
  if (ed) {
    pills.push(
      <span key="ed" className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${ed.cls}`}
            data-testid={`event-editorial-${event.event_id}-${event.editorial_label}`}>
        <MessageSquare className="w-2.5 h-2.5" /> {ed.label}
      </span>
    );
  }
  // 3) Urgency (emergency/breaking dominate)
  if (event.urgency_level === "emergency") {
    pills.push(
      <span key="urg" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-700 text-white animate-pulse"
            data-testid={`event-urgency-${event.event_id}-emergency`}>
        <AlertOctagon className="w-2.5 h-2.5" /> Emergency
      </span>
    );
  } else if (event.urgency_level === "breaking") {
    pills.push(
      <span key="urg" className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white"
            data-testid={`event-urgency-${event.event_id}-breaking`}>
        Breaking
      </span>
    );
  }
  if (!pills.length) return null;
  return <div className="flex flex-wrap items-center gap-1.5 mb-1.5">{pills}</div>;
}

function NeighborhoodChip({ event }) {
  if (!event.neighborhood) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
      data-testid={`event-neighborhood-${event.event_id}`}
      title={`Neighborhood: ${event.neighborhood}`}
    >
      <MapPin className="w-2.5 h-2.5" /> {event.neighborhood}
    </span>
  );
}

export default function EventCard({ event, compact = false }) {
  if (!event) return null;
  const label = categoryLabel(event.categories);
  const cat = primaryCategoryPath(event.categories);
  return (
    <article
      className="flex gap-4 py-4 border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50 -mx-2 px-2 rounded-lg transition-colors"
      data-testid={`newspulse-event-card-${event.event_id}`}
    >
      <Link
        to={`/event/${event.event_id}`}
        className="w-32 sm:w-40 aspect-[4/3] shrink-0 rounded-lg overflow-hidden bg-slate-100"
        data-testid={`newspulse-event-thumb-${event.event_id}`}
      >
        <Thumb src={event.image_url} cats={event.categories} />
      </Link>
      <div className="min-w-0 flex-1">
        <MetaBadges event={event} />
        <div className="flex items-center gap-2 text-[11px] flex-wrap">
          <ImportanceDot score={event.importance} />
          {label && (
            <Link
              to={`/topic/${cat}`}
              className="font-bold uppercase tracking-widest text-blue-600 hover:text-blue-700"
              data-testid={`newspulse-event-category-${event.event_id}`}
            >
              {label}
            </Link>
          )}
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">{timeAgo(event.last_updated_at || event.occurred_at)}</span>
          {event.developing && (
            <>
              <span className="text-slate-400">·</span>
              <span className="text-amber-600 font-semibold">Developing</span>
            </>
          )}
          <NeighborhoodChip event={event} />
          <RegionChip event={event} />
        </div>
        <Link
          to={`/event/${event.event_id}`}
          className="block mt-1 hover:text-blue-700 transition-colors"
          data-testid={`newspulse-event-headline-${event.event_id}`}
        >
          <h3 className={`${compact ? "text-base" : "text-lg sm:text-xl"} font-bold leading-snug text-slate-900`}>
            {event.headline}
          </h3>
        </Link>
        {!compact && event.summary && (
          <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-2">
            {event.summary}
          </p>
        )}
        <HealthPills event={event} />
      </div>
    </article>
  );
}
